import { useEffect, useState } from 'react';

import type { SavedAnalysisDetail } from '@smartapply/shared';

import { DiagnosisView } from '../components/diagnosis/DiagnosisView.js';
import { PageShell } from '../components/layout/PageShell.js';
import { Stamp } from '../components/ui/Stamp.js';
import { SmartApplyApiError } from '../lib/api-client.js';
import {
  archiveAnalysis,
  deleteAnalysis,
  getAnalysis,
  setAnalysisLabel,
} from '../lib/analyses-client.js';
import { useRoute } from '../lib/route.js';

interface AnalysisDetailRouteProps {
  id: string;
}

export function AnalysisDetailRoute({ id }: AnalysisDetailRouteProps) {
  const { navigate } = useRoute();
  const [data, setData] = useState<SavedAnalysisDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    getAnalysis(id)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setLabelDraft(d.label ?? '');
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao carregar.');
      });
    return () => {
      alive = false;
    };
  }, [id]);

  async function handleSaveLabel() {
    if (!data) return;
    setBusy(true);
    try {
      await setAnalysisLabel(data.id, labelDraft || null);
      setData({ ...data, label: labelDraft || null });
      setEditingLabel(false);
    } catch (err) {
      setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao atualizar rótulo.');
    } finally {
      setBusy(false);
    }
  }

  async function handleArchive() {
    if (!data) return;
    if (!confirm('Arquivar esta análise? Você poderá vê-la depois com o filtro arquivadas=true.'))
      return;
    setBusy(true);
    try {
      await archiveAnalysis(data.id);
      navigate('/analises');
    } catch (err) {
      setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao arquivar.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!data) return;
    if (!confirm('Apagar esta análise para sempre? Não dá pra desfazer.')) return;
    setBusy(true);
    try {
      await deleteAnalysis(data.id);
      navigate('/analises');
    } catch (err) {
      setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao apagar.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageShell>
      <button
        type="button"
        onClick={() => navigate('/analises')}
        className="mb-6 font-sans text-sm text-[var(--ink-secondary)] underline-offset-4 hover:underline"
      >
        ← voltar pro histórico
      </button>

      {error && <p className="mb-4 font-sans text-sm text-[var(--accent-crimson)]">{error}</p>}

      {!data && !error && (
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          carregando...
        </p>
      )}

      {data && (
        <>
          <header className="mb-8 flex flex-wrap items-start justify-between gap-4">
            <div className="flex-1">
              <p className="mb-2 inline-block">
                <Stamp>análise salva · {new Date(data.createdAt).toLocaleDateString('pt-BR')}</Stamp>
              </p>
              {editingLabel ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={labelDraft}
                    onChange={(e) => setLabelDraft(e.target.value)}
                    maxLength={120}
                    autoFocus
                    placeholder="rótulo da análise"
                    className="min-w-[260px] rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-sans text-sm focus:border-[var(--accent-saffron)] focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => void handleSaveLabel()}
                    disabled={busy}
                    className="rounded-sm bg-[var(--ink-primary)] px-3 py-2 font-sans text-xs text-[var(--bg-paper)]"
                  >
                    salvar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingLabel(false);
                      setLabelDraft(data.label ?? '');
                    }}
                    className="font-sans text-xs text-[var(--ink-tertiary)] underline-offset-4 hover:underline"
                  >
                    cancelar
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditingLabel(true)}
                  className="text-left"
                  title="clique pra editar"
                >
                  <h1 className="font-serif text-[28px] font-medium leading-tight text-[var(--ink-primary)] sm:text-[32px]">
                    {data.label || 'Análise sem rótulo'}
                  </h1>
                  <p className="mt-1 font-sans text-xs text-[var(--ink-tertiary)] underline-offset-4 hover:underline">
                    editar rótulo
                  </p>
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => void handleArchive()}
                disabled={busy}
                className="font-sans text-xs text-[var(--ink-secondary)] underline-offset-4 hover:underline"
              >
                arquivar
              </button>
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={busy}
                className="font-sans text-xs text-[var(--accent-crimson)] underline-offset-4 hover:underline"
              >
                apagar
              </button>
            </div>
          </header>

          <DiagnosisView
            output={data.output}
            onReset={() => navigate('/analises')}
            readOnly
          />
        </>
      )}
    </PageShell>
  );
}
